<template>
  <custom-popover
    :top="top"
    :left="left"
    class="field-actions"
    @close="hideActionFields"
  >
    <permissions
      v-show="permissions"
      :loading="fetchingData"
      :object="fetchedData"
      :event-type="eventTypes.UpdateObjectPermission"
      :account-id="accountId"
      @close="closePermissions"
      @closeCompletely="hideActionFields"
      @updated="updated"
    />
    <div v-show="!permissions">
      <div
        v-if="!isFolder"
        class="directory-field-actions-item"
      >
        <a @click="$emit('quickShare')">
          <GjIcon name="Share" />
          Quick share
        </a>
      </div>
      <div class="directory-field-actions-item">
        <a
          v-clipboard:copy="item.url"
          v-clipboard:success="copied"
        >
          <GjIcon name="Copy" />
          Copy URL
        </a>
      </div>
      <div
        v-if="!isFolder"
        class="directory-field-actions-item"
      >
        <span
          v-show="downloading"
          class="loading justify-content-start"
        >
          <GjIcon name="LoaderAlt" /> Downloading
        </span>
        <a
          v-show="!downloading"
          @click="initDownload"
        >
          <GjIcon name="Download" />
          Download
        </a>
      </div>
      <div
        v-if="!isFolder"
        class="directory-field-actions-item"
      >
        <a @click="openPermissions">
          <span>
            <GjIcon name="Lock" />
            Permissions
          </span>
        </a>
      </div>
      <div
        v-if="rename"
        class="directory-field-actions-item"
      >
        <a @click="$emit('rename', $event)">
          <GjIcon name="Paragraph" />
          Rename
        </a>
      </div>
      <div
        v-if="move"
        class="directory-field-actions-item"
      >
        <a @click="$emit('move')">
          <GjIcon name="Reply" />
          Move
        </a>
      </div>
      <div
        v-if="copy"
        class="directory-field-actions-item"
      >
        <a @click="$emit('copy')">
          <GjIcon name="Copy" />
          Copy
        </a>
      </div>
      <div
        v-if="remove"
        class="directory-field-actions-item"
      >
        <a @click="$emit('remove')">
          <GjIcon name="Delete" />
          Remove
        </a>
      </div>
    </div>
  </custom-popover>
</template>

<script>
import fileHelpers from '@/mixins/file-helpers'
import helpers from '@/mixins/helpers'
import Permissions from './Permissions.vue'
import CustomPopover from '@/components/popup/CustomPopover.vue'

export default {
  name: 'DirectoryFieldActions',
  components: {
    Permissions,
    CustomPopover,
  },
  mixins: [fileHelpers, helpers],
  props: {
    objectName: {
      default: null,
      type: String,
    },
    remove: {
      default: true,
      type: Boolean,
    },
    move: {
      default: true,
      type: Boolean,
    },
    rename: {
      default: true,
      type: Boolean,
    },
    copy: {
      default: true,
      type: Boolean,
    },
    isFolder: {
      default: true,
      type: Boolean,
    },
    item: {
      type: Object,
      default: null,
    },
    rect: {
      type: DOMRect,
      default: null,
    },
    tableRectLeft: {
      type: Number,
      default: 0,
    },
    tableRectTop: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      permissions: false,
      actionsRef: 'directory-actions-dd',
      fetchingData: true,
      fetchedData: null,
      downloading: false,
      accountId: 0,
    }
  },
  computed: {
    popoverTarget() {
      return `permissions-${this.item.key}`
    },
    bucketName() {
      return this.$route.params.id
    },
    eventTypes() {
      return this.$store.state.accountObjects.eventTypes
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    top() {
      return this.rect.top - this.tableRectTop
    },
    left() {
      return this.rect.left - this.tableRectLeft - 200
    },
  },
  created() {
    this.accountId = this.$route.params.accountId
  },
  methods: {
    updated(values) {
      this.fetchedData = { ...this.fetchedData, ...values }
      this.$emit('updated', this.fetchedData.key)
    },
    closePermissions() {
      this.permissions = false
    },
    openPermissions() {
      this.permissions = true
      if (!this.fetchedData) {
        this.fetchingData = true
        this.fetchData().catch(err => {
          this.errorToast(
            err.response?.data?.errors[0]
              || "Couldn't access permissions. Please try again.",
          )
          this.permissionError = true
        })
      }
    },
    fetchData() {
      return new Promise((resolve, reject) => {
        const query = {
          accountId: this.accountId,
          ...this.item,
          bucketName: this.bucketName,
        }

        this.$store
          .dispatch('accountObjects/fetchObjectInfo', query)
          .then(res => {
            this.fetchedData = res.data.result
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            this.fetchingData = false
          })
      })
    },
    initDownload() {
      this.downloading = true
      if (!this.fetchedData) {
        this.fetchData()
          .then(() => {
            this.download()
          })
          .catch(() => {
            this.errorToast('Error downloading file. Try again...')
          })
      } else {
        this.download()
      }
    },
    download() {
      fetch(this.fetchedData.previewLink)
        .then(resp => resp.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.style.display = 'none'
          a.href = url
          a.download = this.objectName
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
        })
        .catch(() => {})
    },
    hideActionFields() {
      this.$emit('close')
    },
    copied() {
      this.$emit('copied')
    },
  },
}
</script>

<style lang="scss" scoped>
.field-actions {
  max-width: 200px;
  &::v-deep {
    .directory-field-actions-item {
      padding: 0.625rem 1rem;
    }
  }
}
</style>
